export const content = {
  calendar: {
    title: "Content calendar",
    description: "Plan, schedule and reschedule posts across every account. Drag a post to move it.",
    month: "Month",
    week: "Week",
    agenda: "Agenda",
    today: "Today",
    prev: "Previous",
    next: "Next",
    newPost: "Create post",
    allAccounts: "All accounts",
    more: "+{{n}} more",
    empty: "Nothing scheduled in this period",
    emptyHint: "Plan your next post: pick a slot or let Malfak suggest the best time.",
    bestTime: "Best time",
    bestTimeHint: "Your audience is most active around this hour",
    dropHere: "Drop to reschedule",
    rescheduled: "Rescheduled to {{when}}",
    rescheduleFailed: "Couldn't reschedule this post",
    cantMovePublished: "Published posts can't be moved",
    cantMovePast: "Pick a time in the future",
    addAt: "Create a post at {{when}}",
    legend: "Legend",
    noAgenda: "No upcoming posts",
    upcoming: "Upcoming",
    past: "Past",
  },
  status: {
    draft: "Draft",
    scheduled: "Scheduled",
    published: "Published",
    failed: "Failed",
    publishing: "Publishing",
    in_review: "In review",
  },
  composer: {
    title: "Create post",
    description: "Write once, publish to every account, with per-account tweaks.",
    targets: "Publish to",
    targetsHint: "Pick one or more accounts.",
    noTargets: "Pick at least one account",
    quota: "{{used}} of {{quota}} API posts in the last 24 h",
    unsupportedType: "{{account}} can't publish this type",
    type: "Post type",
    media: "Media",
    mediaHint: "Drop images or videos here, or browse. Up to {{max}} items.",
    browse: "Browse files",
    library: "Media library",
    libraryEmpty: "Your library is empty. Upload files or generate images in AI Studio.",
    uploading: "Uploading…",
    uploadFailed: "Upload failed",
    removeMedia: "Remove",
    reorderHint: "Drag to reorder the carousel",
    tooMany: "Carousels hold up to {{max}} items",
    needMedia: "Add at least one image or video",
    crop: "Crop",
    caption: "Caption",
    captionPlaceholder: "Write a caption… Start with a hook, end with a call to action.",
    override: "Custom caption for @{{account}}",
    overrideHint: "Leave empty to use the main caption.",
    mainCaption: "Main",
    chars: "{{n}} / 2,200 characters",
    hashtags: "{{n}} / 30 hashtags",
    mentions: "{{n}} / 20 mentions",
    ai: "Write with AI",
    aiBrief: "What's the post about?",
    aiBriefPlaceholder: "e.g. new autumn painting, 90×120, one piece only",
    tone: "Tone",
    tones: { casual: "Casual", professional: "Professional", playful: "Playful", luxury: "Luxury", bold: "Bold" },
    language: "Language",
    withHashtags: "Add hashtags",
    generate: "Generate captions",
    generating: "Writing…",
    useThis: "Use this",
    when: "When",
    publishNow: "Publish now",
    schedule: "Schedule",
    date: "Date",
    time: "Time",
    timezone: "Times in {{tz}}",
    bestTimes: "Suggested times",
    inPast: "That time has already passed",
    submitNow: "Publish now",
    submitSchedule: "Schedule post",
    created: "Post scheduled for {{when}}",
    publishing: "Publishing to {{n}} account(s)…",
    createFailed: "Couldn't create the post",
    draftSaved: "Draft saved on this device",
    draftRestored: "Draft restored",
    discard: "Discard draft",
    preview: "Preview",
    previewFor: "Preview as @{{account}}",
    more: "more",
    likedBy: "Liked by your followers",
    viewComments: "View all comments",
    justNow: "Just now",
    checklist: "Ready to publish",
  },
  crops: { square: "Square 1:1", portrait: "Portrait 4:5", landscape: "Landscape 1.91:1", vertical: "Vertical 9:16" },
  score: {
    title: "Post score",
    excellent: "Excellent",
    good: "Good",
    fair: "Needs work",
    poor: "Weak",
    empty: "Write a caption to see its score",
    fix: "Fix",
    checks: {
      hook: "Open with a short hook: the first line shows before “more” (≤ 125 characters).",
      length: "Aim for 60–1,300 characters.",
      cta: "Add a call to action: save, comment, DM or link in bio.",
      hashtags: "Use 3–15 relevant hashtags.",
      engagement: "Ask a question to invite comments.",
      readability: "Break long captions into short paragraphs.",
      emoji: "One or two emoji add warmth.",
      limits: "Stay within Instagram limits (2,200 chars, 30 hashtags, 20 mentions).",
      reel: "Reels: keep captions short.",
    },
    fixes: {
      cta: "Save this post for later 📌",
      engagement: "What do you think? 👇",
      hashtags: "#art #design #homedecor",
      emoji: "✨",
    },
  },
  posts: {
    title: "Posts",
    description: "Everything you've published, scheduled or drafted, on every account.",
    grid: "Grid",
    list: "List",
    account: "Account",
    status: "Status",
    allStatuses: "All statuses",
    empty: "No posts yet",
    emptyHint: "Create your first post or connect an account to import past posts.",
    imported: "Imported",
    caption: "Caption",
    date: "Date",
    reach: "Reach",
    likes: "Likes",
    comments: "Comments",
    loadMore: "Load more",
  },
  detail: {
    back: "All posts",
    publications: "Publications",
    noMetrics: "Metrics appear after publishing",
    comments: "Comments",
    noComments: "No comments yet",
    rules: "Rules on this post",
    noRules: "Only account-wide rules apply to this post",
    allAccounts: "All accounts",
    thisPost: "This post",
    reschedule: "Reschedule",
    cancel: "Cancel post",
    cancelConfirm: "Cancel this scheduled post?",
    cancelled: "Post cancelled",
    openOnInstagram: "Open on Instagram",
    error: "Error",
    hidden: "Hidden",
    reply: "Reply by the account",
    scheduledFor: "Scheduled for {{when}}",
    publishedAt: "Published {{when}}",
    createdBy: "Created in Malfak",
    engagementRate: "Engagement rate",
  },
  /** ALP-84 S6: calmer post page (header with one primary, tabs) and posts list. */
  postPage: {
    untitled: "Post without a caption",
    tabs: { overview: "Overview", comments: "Comments", insights: "Insights" },
    copyLink: "Copy Instagram link",
    copied: "Link copied",
    cancelHint: "It goes back to drafts and won't be published.",
  },
  postsList: {
    search: "Search captions",
    more: "More",
    reloadAllTitle: "Reload every imported post from Instagram?",
    reloadAllHint: "This re-reads up to 500 posts and their comments from Instagram. It can take a few minutes; you can keep working.",
    reloadAccountTitle: "Reload @{{user}}'s posts from Instagram?",
    noMatch: "No posts match",
    noMatchHint: "Try another word, account or status.",
  },
  composerCalm: {
    perAccount: "Different caption per account",
    discardTitle: "Discard this draft?",
    discardHint: "The caption and media you added here are removed. This can't be undone.",
  },
  // ALP-83: tag counts above the post's comment list
  tagChips: { label: "Comments by tag", filter: "Show only {{tag}}", clear: "Show all comments (now: {{tag}})" },
};
